/**
 * Safe Supabase client that doesn't crash in restricted storage environments
 * (e.g., Safari Private Browsing, in-app browsers, or storage-disabled settings).
 * 
 * This replaces the auto-generated client.ts which directly references localStorage
 * and can throw SecurityError before React even starts rendering.
 * 
 * Features:
 * - Memory fallback when localStorage fails (Safari Private Browsing)
 * - Graceful degradation for restricted environments
 * - Session persistence with automatic recovery
 * - Explicit storage availability testing
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { safeStorage } from '@/lib/safeStorage';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Test if localStorage is actually available and functional.
 * Some browsers (Safari Private Browsing) have localStorage defined
 * but throw when you try to use it.
 */
const isLocalStorageAvailable = (() => {
  try {
    const testKey = '__supabase_storage_test__';
    if (typeof localStorage === 'undefined') {
      console.warn('[SafeClient] localStorage is undefined');
      return false;
    }
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn('[SafeClient] localStorage is not available:', e);
    return false;
  }
})();

// Log storage status on initialization
if (!isLocalStorageAvailable) {
  console.warn('[SafeClient] Using memory-only storage fallback. Sessions will not persist across page reloads.');
}

/**
 * In-memory fallback storage for environments where localStorage fails.
 * This ensures sessions can still work within a single browser session
 * even if persistent storage is blocked.
 */
const memoryStorage: Record<string, string> = {};

/**
 * Custom storage adapter that wraps localStorage access in try/catch
 * to prevent crashes in restricted environments.
 * Falls back to in-memory storage when localStorage is unavailable.
 */
const safeStorageAdapter = {
  getItem: (key: string): string | null => {
    // If localStorage is known to be unavailable, only use memory
    if (!isLocalStorageAvailable) {
      return memoryStorage[key] ?? null;
    }
    // First try localStorage via safeStorage
    const stored = safeStorage.getItem(key);
    // Fall back to memory if localStorage returns null (could be blocked)
    return stored ?? memoryStorage[key] ?? null;
  },
  setItem: (key: string, value: string): void => {
    // Always store in memory as backup
    memoryStorage[key] = value;
    // Also try to persist to localStorage if available
    if (isLocalStorageAvailable) {
      safeStorage.setItem(key, value);
    }
  },
  removeItem: (key: string): void => {
    // Remove from both memory and localStorage
    delete memoryStorage[key];
    if (isLocalStorageAvailable) {
      safeStorage.removeItem(key);
    }
  },
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/safeClient";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: safeStorageAdapter,
    persistSession: true,
    autoRefreshToken: true,
  }
});

// Export storage availability for debugging
export const storageStatus = {
  isLocalStorageAvailable,
  usingMemoryFallback: !isLocalStorageAvailable,
};
